import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AIStateProvider } from './contexts/AIStateContext';
import { SuperAdminAuthProvider } from './contexts/SuperAdminAuthContext';
import { SuperAdminThemeProvider } from './contexts/SuperAdminThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import { SubscriptionGuard } from './components/SubscriptionGuard';
import { HRRouteGuard } from './components/HRRouteGuard';
import { AppShell } from './components/AppShell';
// Cookie consent removed
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { InvalidRoute } from './components/ui/page-guards';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { SectionErrorBoundary } from './components/SectionErrorBoundary';
import SessionProvider from './components/SessionProvider';
import ImpersonationBanner from './components/ImpersonationBanner';
import SuperAdminLogin from './pages/SuperAdminLogin';

// Register toast globally for API interceptor
window.__sonnerToast = (msg) => toast.error(msg);

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
// Dashboard components
import OverviewDashboard from './pages/dashboards/OverviewDashboard';

import Billing from './pages/Billing';
import Leads from './pages/Leads';
import Audience from './pages/Audience';
import LeadDetails from './pages/LeadDetails';
import LeadSettings from './pages/LeadSettings';
import LeadSearch from './pages/LeadSearch';
import Clients from './pages/Clients';
import ClientDetails from './pages/ClientDetails';
import Quotes from './pages/Quotes';
import QuoteDetails from './pages/QuoteDetails';
import Invoices from './pages/Invoices';
import InvoiceDetails from './pages/InvoiceDetails';
import Deals from './pages/Deals';
import DealDetails from './pages/DealDetails';
import Campaigns from './pages/Campaigns';
import CampaignAnalytics from './pages/CampaignAnalytics';
import AIWorkflows from './pages/Workflows';
import LinkedInWorkspace from './pages/LinkedInWorkspace';
import ResponseInbox from './pages/ResponseInbox';
import WhatsAppWorkspace from './pages/WhatsAppWorkspace';
import Team from './pages/Team';
import Users from './pages/hr/Users';
import UserDetails from './pages/hr/UserDetails';
// FROZEN: HR Attendance/Salary/Leave/Settings — backend APIs frozen
import Schedule from './pages/Schedule';
import ScheduleCalendar from './pages/ScheduleCalendar';
import EventsList from './pages/EventsList';
import TasksList from './pages/TasksList';
import FollowUpsList from './pages/FollowUpsList';
import VisitsList from './pages/VisitsList';
import MeetingsList from './pages/MeetingsList';
import Reports from './pages/Reports';
import LeadReports from './pages/reports/LeadReports';
import InvoiceReports from './pages/reports/InvoiceReports';
import QuoteReports from './pages/reports/QuoteReports';
import ClientReports from './pages/reports/ClientReports';
import PaymentReports from './pages/reports/PaymentReports';
import GenericReport from './pages/reports/GenericReport';
import SearchTuningReport from './pages/reports/SearchTuningReport';
import OutreachDashboard from './pages/reports/OutreachDashboard';
import SalesReportBuilder from './pages/reports/SalesReportBuilder';
import DealApprovals from './pages/settings/DealApprovals';
import CPQPricingRules from './pages/settings/CPQPricingRules';
import CommissionDashboard from './pages/settings/CommissionDashboard';
import ForecastingDashboard from './pages/reports/ForecastingDashboard';
import EmailAnalytics from './pages/reports/EmailAnalytics';
import SalesReports from './pages/reports/SalesReports';
import EngagementReports from './pages/reports/EngagementReports';
import LeoImpactReport from './pages/reports/LeoImpactReport';
import FinanceReports from './pages/reports/FinanceReports';
// Subscriptions removed - redirected to /billing
import AITemplateHub from './pages/AITemplateHub';
import BrainDigest from './pages/Brain/BrainDigest';
import VoiceCallFlows from './pages/Brain/VoiceCallFlows';
import TemplateHub from './pages/Brain/TemplateHub';
import BrainCommandCenter from './pages/Brain/BrainCommandCenter';
// FROZEN: Ecommerce — backend API frozen
// FSMDashboard, TicketsDashboard removed - dashboards feature removed
import Profile from './pages/Profile';
import HelpHub from './pages/HelpHub';
// UserDashboard removed - dashboards feature removed
import Payments from './pages/Payments';
import Taxes from './pages/Taxes';
import QuoteStatusSettings from './pages/Account/QuoteStatusSettings';
import InvoiceStatusSettings from './pages/Account/InvoiceStatusSettings';
import Supports from './pages/settings/Supports';
import KnowledgeBase from './pages/settings/KnowledgeBase';
import VoiceAISettings from './pages/settings/VoiceAISettings';
import VoiceAIAnalytics from './pages/settings/VoiceAIAnalytics';
import InboundVoiceHub from './pages/settings/InboundVoiceHub';
import CallHistoryDashboard from './pages/settings/CallHistoryDashboard';
import CadenceAnalytics from './pages/CadenceAnalytics';
import RevealAnalyticsDashboard from './pages/RevealAnalyticsDashboard';
import ComplianceSettings from './pages/settings/ComplianceSettings';
import BreachManagement from './pages/settings/BreachManagement';
import ExportsReporting from './pages/settings/ExportsReporting';
import AboutUs from './pages/settings/AboutUs';
import ContactUs from './pages/settings/ContactUs';
import CRMSync from './pages/settings/CRMSync';
import Territories from './pages/settings/Territories';
import SLARules from './pages/settings/SLARules';
import WorkflowsPage from './pages/settings/Workflows';
import SecurityCompliance from './pages/settings/SecurityCompliance';
import CRMBidirectionalSync from './pages/settings/CRMBidirectionalSync';
import ProductsCatalog from './pages/settings/ProductsCatalog';
import RevenueForecast from './pages/RevenueForecast';
import APIKeys from './pages/APIKeys';
// FROZEN: MyAttendance — backend API frozen
import AISettings from './pages/AISettings';
import GovernancePanel from './pages/GovernancePanel';
import IntegrationsPage from './pages/IntegrationsPage';
import DataImportPage from './pages/DataImportPage';
import SocialConnectorsPage from './pages/SocialConnectorsPage';
import AIActivityFeed from './pages/AIActivityFeed';
import BrainAlerts from './pages/BrainAlerts';
import BrainInsightsPage from './pages/BrainInsightsPage';
import ICPDashboard from './pages/ai-agents/ICPDashboard';
import EmailOutbox from './pages/EmailOutbox';
import ExtensionConnect from './pages/ExtensionConnect';
import EmailEngagementDashboard from './pages/EmailEngagementDashboard';
import DocumentLibrary from './pages/DocumentLibrary';
import DocVerification from './pages/DocVerification';
import ICPLearningPage from './pages/ICPLearningPage';
import QuoteSignPage from './pages/QuoteSignPage';
import PipelineHealthDashboard from './pages/PipelineHealthDashboard';
import OrgOverview from './pages/OrgOverview';
import EnterpriseControls from './pages/EnterpriseControls';
// GetStarted removed — onboarding now goes directly to dashboard
import MeetingScheduler from './pages/MeetingScheduler';
import BookingPage from './pages/BookingPage';
import MeetingConfirm from './pages/MeetingConfirm';
import ChatWidget from './pages/ChatWidget';
import LiveConversations from './pages/LiveConversations';

// Onboarding pages
import BrainInit from './pages/onboarding/OnboardingWizard';

// Intelligence Dashboards removed - dashboards feature removed

// Admin pages
import {
  AdminLayout,
  AdminDashboard,
  AdminTenants,
  AdminAgents,
  AdminBudgets,
  AdminConfidence,
  AdminAudit,
  AdminOrganizations,
  AdminOrganizationDetail,
  AdminTenantDetail,
  AdminUserDetail,
  InvestorView,
  AdminApprovals,
  AdminUsers,
  ServiceSubscriptions,
  FounderOps,
  Economics,
  CapabilityInspector,
  AutonomyMargins,
  BillingDrift,
  BillingAudit,
  UsageAudit,
  HelpInsights,
} from './pages/admin';

// Root redirect component - redirects based on auth state
const RootRedirect = () => {
  const token = localStorage.getItem('access_token');
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <AppErrorBoundary>
    <ThemeProvider>
    <AuthProvider>
      <AIStateProvider>
        <CurrencyProvider>
          <PermissionProvider>
            <BrowserRouter>
            <SessionProvider>
            {/* Global impersonation banner — visible on EVERY page including /onboarding/* */}
            <ImpersonationBanner />
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/quote-sign/:token" element={<QuoteSignPage />} />
            
            {/* Onboarding routes - protected but no AppShell */}
            <Route
              path="/onboarding/brain-init"
              element={
                <ProtectedRoute>
                  <BrainInit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/modules"
              element={<Navigate to="/dashboard" replace />}
            />
          {/* Dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppShell>
                  <OverviewDashboard />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route path="/user-dashboard" element={<Navigate to="/dashboard" replace />} />
          
          {/* Protected routes */}
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="lead">
                  <AppShell>
                    <Leads />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audience"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="lead">
                  <AppShell>
                    <Audience />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:id"
            element={
              <ProtectedRoute>
                <AppShell>
                  <LeadDetails />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/discover-leads"
            element={
              <ProtectedRoute>
                <AppShell>
                  <LeadSearch />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads-settings"
            element={
              <ProtectedRoute>
                <AppShell>
                  <LeadSettings />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="lead">
                  <AppShell>
                    <Deals />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals/:id"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="lead">
                  <AppShell>
                    <DealDetails />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Campaigns />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/view/:id"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Campaigns />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/analytics"
            element={
              <ProtectedRoute>
                <AppShell>
                  <CampaignAnalytics />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-conversations"
            element={
              <ProtectedRoute>
                <AppShell>
                  <LiveConversations />
                </AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai-workflows"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AIWorkflows />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/linkedin-outbox"
            element={
              <ProtectedRoute>
                <AppShell>
                  <LinkedInWorkspace />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/responses"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ResponseInbox />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/linkedin-extension"
            element={<Navigate to="/linkedin-outbox?tab=setup" replace />}
          />
          <Route
            path="/whatsapp"
            element={
              <ProtectedRoute>
                <AppShell>
                  <WhatsAppWorkspace />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route path="/whatsapp-inbox" element={<Navigate to="/whatsapp?tab=conversations" replace />} />
          <Route path="/whatsapp-outbox" element={<Navigate to="/whatsapp?tab=queue" replace />} />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="client">
                  <AppShell>
                    <Clients />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ClientDetails />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="account">
                  <AppShell>
                    <Quotes />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <AppShell>
                  <QuoteDetails />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="account">
                  <AppShell>
                    <Invoices />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <AppShell>
                  <InvoiceDetails />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Team />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr/users"
            element={
              <ProtectedRoute>
                  <AppShell>
                    <Users />
                  </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr/users/:id"
            element={
              <ProtectedRoute>
                  <AppShell>
                    <UserDetails />
                  </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ScheduleCalendar />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule/events"
            element={
              <ProtectedRoute>
                <AppShell>
                  <EventsList />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule/tasks"
            element={
              <ProtectedRoute>
                <AppShell>
                  <TasksList />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule/followups"
            element={
              <ProtectedRoute>
                <AppShell>
                  <FollowUpsList />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule/visits"
            element={
              <ProtectedRoute>
                <AppShell>
                  <VisitsList />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule/meetings"
            element={
              <ProtectedRoute>
                <AppShell>
                  <MeetingsList />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Profile />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <AppShell>
                  <HelpHub />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/get-started"
            element={
              <Navigate to="/dashboard" replace />
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Reports />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* ========== GROUPED REPORT PAGES ========== */}
          <Route
            path="/reports/sales"
            element={
              <ProtectedRoute>
                <AppShell>
                  <SalesReports />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/engagement"
            element={
              <ProtectedRoute>
                <AppShell>
                  <EngagementReports />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/leo-impact"
            element={
              <ProtectedRoute adminOnly>
                <AppShell>
                  <LeoImpactReport />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/finance"
            element={
              <ProtectedRoute>
                <AppShell>
                  <FinanceReports />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* ========== OLD REPORT REDIRECTS ========== */}
          <Route
            path="/reports/leads"
            element={<Navigate to="/reports/engagement" replace />}
          />
          <Route
            path="/reports/clients"
            element={<Navigate to="/reports/finance" replace />}
          />
          <Route
            path="/reports/quotes"
            element={<Navigate to="/reports/finance" replace />}
          />
          <Route
            path="/reports/invoices"
            element={<Navigate to="/reports/finance" replace />}
          />
          <Route
            path="/reports/payments"
            element={<Navigate to="/reports/finance" replace />}
          />
          <Route
            path="/reports/purchase-orders"
            element={
              <ProtectedRoute>
                <AppShell>
                  <GenericReport title="Purchase Order Reports" endpoint="purchase-orders" dataKey="purchase_orders" />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/search-tuning"
            element={
              <ProtectedRoute>
                <AppShell>
                  <SearchTuningReport />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/outreach"
            element={<Navigate to="/reports/engagement" replace />}
          />
          <Route
            path="/reports/sales"
            element={
              <ProtectedRoute>
                <AppShell>
                  <GenericReport title="Sales Reports" endpoint="sales" dataKey="quotes" />
                </AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscriptions"
            element={
              <Navigate to="/billing" replace />
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Billing />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="account">
                  <AppShell>
                    <Payments />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/taxes"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="account">
                  <AppShell>
                    <Taxes />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/quote-status-settings"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="account">
                  <AppShell>
                    <QuoteStatusSettings />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/invoice-status-settings"
            element={
              <ProtectedRoute>
                <SubscriptionGuard requiredModule="account">
                  <AppShell>
                    <InvoiceStatusSettings />
                  </AppShell>
                </SubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/supports"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Supports />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* OLD AI Routes removed - now redirect to /ai-agents/* via legacy redirects below */}
          <Route
            path="/settings/about"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AboutUs />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/contact"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ContactUs />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/crm-sync"
            element={
              <ProtectedRoute>
                <AppShell>
                  <CRMSync />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/territories"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Territories />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/sla-rules"
            element={
              <ProtectedRoute>
                <AppShell>
                  <SLARules />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/workflows"
            element={<Navigate to="/ai-workflows" replace />
            }
          />
          <Route
            path="/settings/security"
            element={
              <ProtectedRoute>
                <AppShell>
                  <SecurityCompliance />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/crm-sync-v2"
            element={
              <ProtectedRoute>
                <AppShell>
                  <CRMBidirectionalSync />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/products"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ProductsCatalog />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/deal-approvals"
            element={
              <ProtectedRoute>
                <AppShell>
                  <DealApprovals />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/cpq"
            element={
              <ProtectedRoute>
                <AppShell>
                  <CPQPricingRules />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/sales-analytics"
            element={<Navigate to="/reports/sales" replace />}
          />
          <Route
            path="/reports/forecasting"
            element={<Navigate to="/reports/sales" replace />}
          />
          <Route
            path="/reports/email-analytics"
            element={<Navigate to="/reports/engagement" replace />}
          />
          <Route
            path="/settings/commissions"
            element={
              <ProtectedRoute>
                <AppShell>
                  <CommissionDashboard />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/revenue-forecast"
            element={<Navigate to="/reports/sales" replace />}
          />
          <Route
            path="/developer/api-keys"
            element={
              <ProtectedRoute>
                <AppShell>
                  <APIKeys />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* OLD AI Settings/Activity routes removed - now use /ai-agents/* routes */}
          <Route
            path="/org/overview"
            element={
              <ProtectedRoute>
                <AppShell>
                  <OrgOverview />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* OLD Enterprise Controls route - redirected to /ai-agents/enterprise-controls */}
          
          {/* ========== AI AGENTS SECTION ROUTES ========== */}
          <Route
            path="/ai-agents/enterprise-controls"
            element={<Navigate to="/brain/command-center" replace />}
          />
          <Route
            path="/ai-agents/settings"
            element={<Navigate to="/brain/command-center" replace />}
          />
          <Route
            path="/brain/alerts"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BrainAlerts />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/insights"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BrainInsightsPage />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/icp"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ICPDashboard />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/extension-connect"
            element={
              <ProtectedRoute>
                <ExtensionConnect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/email-outbox"
            element={
              <ProtectedRoute>
                <AppShell>
                  <EmailOutbox />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/email-engagement"
            element={<Navigate to="/campaigns/analytics?tab=email" replace />}
          />
          <Route
            path="/document-library"
            element={
              <ProtectedRoute>
                <AppShell>
                  <DocumentLibrary />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doc-verification"
            element={
              <ProtectedRoute>
                <AppShell>
                  <DocVerification />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-agents/activity"
            element={<Navigate to="/brain/command-center" replace />}
          />
          <Route
            path="/brain/icp-learning"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ICPLearningPage />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline-health"
            element={<Navigate to="/reports/sales" replace />}
          />
          <Route
            path="/ai-agents/knowledge-base"
            element={
              <ProtectedRoute>
                <AppShell>
                  <KnowledgeBase />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-agents/voice-settings"
            element={
              <ProtectedRoute>
                <AppShell>
                  <VoiceAISettings />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-agents/inbound-voice"
            element={
              <ProtectedRoute>
                <AppShell>
                  <InboundVoiceHub />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/calculator"
            element={<Navigate to="/billing?tab=plans" replace />}
          />
          <Route
            path="/ai-agents/voice-analytics"
            element={<Navigate to="/campaigns/analytics?tab=voice" replace />}
          />
          <Route
            path="/ai-agents/call-history"
            element={
              <ProtectedRoute>
                <AppShell>
                  <CallHistoryDashboard />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-agents/compliance"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ComplianceSettings />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-agents/breach-management"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BreachManagement />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-agents/exports"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ExportsReporting />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* ========== END AI AGENTS ROUTES ========== */}
          
          {/* ========== CADENCE ANALYTICS ========== */}
          <Route
            path="/ai-agents/cadence-analytics"
            element={<Navigate to="/campaigns/analytics?tab=cadence" replace />}
          />

          {/* ========== REVEAL ANALYTICS ========== */}
          <Route
            path="/ai-agents/reveal-analytics"
            element={
              <ProtectedRoute>
                <AppShell>
                  <RevealAnalyticsDashboard />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* ========== AI MEETING SCHEDULER ========== */}
          <Route
            path="/ai-agents/meeting-scheduler"
            element={
              <ProtectedRoute>
                <AppShell>
                  <MeetingScheduler />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* ========== PUBLIC BOOKING PAGE (No Auth) ========== */}
          <Route path="/book/:tenantId" element={<BookingPage />} />
          <Route path="/meeting/confirm/:token" element={<MeetingConfirm />} />

          {/* ========== PUBLIC EMBEDDABLE CHAT WIDGET (No Auth) ========== */}
          <Route path="/widget/chat/:token" element={<ChatWidget />} />

          {/* ========== GOVERNANCE CONTROL PANEL ========== */}
          <Route
            path="/governance"
            element={<Navigate to="/brain/command-center" replace />}
          />

          {/* ========== INTEGRATIONS ========== */}
          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <AppShell>
                  <IntegrationsPage />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* ========== DATA IMPORT ========== */}
          <Route
            path="/data-import"
            element={
              <ProtectedRoute>
                <AppShell>
                  <DataImportPage />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* ========== SOCIAL CONNECTORS ========== */}
          <Route
            path="/social-connectors"
            element={
              <ProtectedRoute>
                <AppShell>
                  <SocialConnectorsPage />
                </AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/marketplace"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AITemplateHub />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/digest"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BrainDigest />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/voice-flows"
            element={
              <ProtectedRoute>
                <AppShell>
                  <VoiceCallFlows />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/templates"
            element={
              <ProtectedRoute>
                <AppShell>
                  <TemplateHub />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brain/command-center"
            element={
              <ProtectedRoute>
                <AppShell>
                  <BrainCommandCenter />
                </AppShell>
              </ProtectedRoute>
            }
          />
          {/* Brain Chat alias — chat UI lives on the home dashboard via BrainChatHero */}
          <Route path="/brain/chat" element={<Navigate to="/dashboard?focus=brain-chat" replace />} />
          <Route path="/brain" element={<Navigate to="/brain/command-center" replace />} />
          
          {/* Legacy AI routes - redirect to new AI Agents section */}
          <Route path="/settings/ai" element={<Navigate to="/ai-agents/settings" replace />} />
          <Route path="/ai/activity" element={<Navigate to="/ai-agents/activity" replace />} />
          <Route path="/ai-activity" element={<Navigate to="/ai-agents/activity" replace />} />
          <Route path="/enterprise/controls" element={<Navigate to="/ai-agents/enterprise-controls" replace />} />
          <Route path="/settings/knowledge-base" element={<Navigate to="/ai-agents/knowledge-base" replace />} />
          <Route path="/settings/voice-ai" element={<Navigate to="/ai-agents/voice-settings" replace />} />
          <Route path="/settings/voice-analytics" element={<Navigate to="/ai-agents/voice-analytics" replace />} />
          <Route path="/settings/compliance" element={<Navigate to="/ai-agents/compliance" replace />} />
          <Route path="/settings/exports" element={<Navigate to="/ai-agents/exports" replace />} />

          {/* SuperAdmin Login - Separate from CRM login */}
          <Route path="/superadmin/login" element={
            <SuperAdminAuthProvider>
              <SuperAdminThemeProvider>
                <SuperAdminLogin />
              </SuperAdminThemeProvider>
            </SuperAdminAuthProvider>
          } />

          {/* Governance Routes - AI SuperAdmin (requires superadmin role) */}
          {/* ALL governance routes wrapped in SuperAdminAuthProvider + SuperAdminThemeProvider for ISOLATED auth & theme */}
          <Route
            path="/governance/dashboard"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/tenants"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminTenants />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/approvals"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminApprovals />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/users"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminUsers />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/agents"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminAgents />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/budgets"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminBudgets />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/billing-drift"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <BillingDrift />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/billing-audit"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <BillingAudit />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/usage-audit"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <UsageAudit />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/help-insights"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <HelpInsights />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/confidence"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminConfidence />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/audit"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminAudit />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/service-subscriptions"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <ServiceSubscriptions />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/investor"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <InvestorView />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/founder-ops"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <FounderOps />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/economics"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <Economics />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/capability"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <CapabilityInspector />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/autonomy-margins"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AutonomyMargins />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/organizations"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminOrganizations />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/organizations/:orgId"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminOrganizationDetail />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/tenants/:tenantId"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminTenantDetail />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route
            path="/governance/users/:userId"
            element={
              <SuperAdminAuthProvider>
                <SuperAdminThemeProvider>
                  <SuperAdminRoute>
                    <AdminLayout>
                      <AdminUserDetail />
                    </AdminLayout>
                  </SuperAdminRoute>
                </SuperAdminThemeProvider>
              </SuperAdminAuthProvider>
            }
          />
          <Route path="/governance" element={<Navigate to="/governance/dashboard" replace />} />
          
          {/* Developer route redirect - /developer redirects to /developer/api-keys */}
          <Route path="/developer" element={<Navigate to="/developer/api-keys" replace />} />
          
          {/* Redirect root based on auth state */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Aliases / redirects — guard against dead-end URLs in the wild */}
          <Route path="/accounts" element={<Navigate to="/clients" replace />} />
          
          {/* 404 Catch-all route for invalid URLs */}
          <Route path="*" element={
            <ProtectedRoute>
              <AppShell>
                <InvalidRoute 
                  title="Page Not Found"
                  message="The page you're looking for doesn't exist or has been moved."
                  showHome={true}
                />
              </AppShell>
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster position="top-right" offset={64} />
        </SessionProvider>
      </BrowserRouter>
          </PermissionProvider>
        </CurrencyProvider>
      </AIStateProvider>
    </AuthProvider>
    </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
